import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import axios from 'axios';

import { API_URL } from '../config/apiConfig';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const pingInProgressRef = useRef(false);
  
  // Helper function to check if token has expired based on idle timeout
  const isTokenExpired = () => {
    const tokenTimestamp = localStorage.getItem('tokenTimestamp');
    if (!tokenTimestamp) {
      return true; // No timestamp means token is invalid
    }
    
    const now = Date.now();
    const lastActivity = parseInt(tokenTimestamp, 10);
    const idleTimeout = 5 * 60 * 1000; // 5 minutes default (will be updated from settings)
    
    return (now - lastActivity) > idleTimeout;
  };
  
  // Helper function to update token timestamp
  const updateTokenTimestamp = () => {
    localStorage.setItem('tokenTimestamp', Date.now().toString());
  };

  // Set up axios response interceptor to handle session timeout and token refresh
  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => {
        // Check for new token in header (from auth middleware)
        const newTokenFromHeader = response.headers['x-new-token'];
        if (newTokenFromHeader) {
          const currentToken = localStorage.getItem('token');
          if (newTokenFromHeader !== currentToken) {
            localStorage.setItem('token', newTokenFromHeader);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newTokenFromHeader}`;
            updateTokenTimestamp(); // Update timestamp when token is refreshed
          }
        }
        
        // Also check if response body contains a new token (from login/2FA endpoints)
        if (response.data && response.data.token) {
          const newToken = response.data.token;
          const currentToken = localStorage.getItem('token');
          
          // Only update if token has actually changed to prevent unnecessary updates
          if (newToken !== currentToken) {
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            updateTokenTimestamp(); // Update timestamp when token is refreshed
          }
        }
        return response;
      },
      async error => {
        // Check if the error is due to an expired token or session timeout
        if (error.response && error.response.status === 401) {
          // Check if it's specifically an idle timeout
          const isIdleTimeout = error.response.data && 
                               error.response.data.code === 'IDLE_TIMEOUT';
          
          // For token expired errors (not idle timeout), try to refresh once
          if (!isIdleTimeout && error.response.data.error === 'Token expired') {
            try {
              // Attempt to refresh the session by pinging
              const token = localStorage.getItem('token');
              if (token && !pingInProgressRef.current) {
                pingInProgressRef.current = true;
                const pingResponse = await axios.get(`${API_URL}/api/auth/ping`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                
                if (pingResponse.data && pingResponse.data.token) {
                  localStorage.setItem('token', pingResponse.data.token);
                  axios.defaults.headers.common['Authorization'] = `Bearer ${pingResponse.data.token}`;
                  
                  // Retry the original request with the new token
                  error.config.headers['Authorization'] = `Bearer ${pingResponse.data.token}`;
                  return axios.request(error.config);
                }
              }
            } catch (refreshError) {
              console.error('Token refresh failed:', refreshError);
            } finally {
              pingInProgressRef.current = false;
            }
          }
          
          // Clear token and user data for any 401 error that couldn't be refreshed
          localStorage.removeItem('token');
          localStorage.removeItem('tokenTimestamp');
          delete axios.defaults.headers.common['Authorization'];
          setUser(null);
          
          // If it's an idle timeout, redirect to login with a message
          if (isIdleTimeout) {
            window.location.href = '/login?timeout=idle';
          }
        }
        return Promise.reject(error);
      }
    );
    
    // Add beforeunload event listener to handle abrupt browser closure
    const handleBeforeUnload = (event) => {
      const isLoggingOut = localStorage.getItem('isLoggingOut');
      if (isLoggingOut === 'true') {
        // If we're in the middle of logging out and the page is being unloaded,
        // ensure the token is cleared
        localStorage.removeItem('token');
        localStorage.removeItem('tokenTimestamp');
      }
      
      // Mark session as potentially stale when browser/tab is closing
      // This helps detect stale sessions on next app load
      if (!isLoggingOut) {
        localStorage.setItem('sessionClosed', 'true');
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    
    return () => {
      // Clean up interceptor and event listener on unmount
      axios.interceptors.response.eject(interceptor);
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  // Function to ping the server to update activity timestamp (called by IdleTimeoutManager)
  const pingServer = async () => {
    // Prevent multiple concurrent pings
    if (pingInProgressRef.current) {
      return;
    }

    try {
      // Always ping if we have a token, regardless of user state
      const token = localStorage.getItem('token');
      if (token) {
        pingInProgressRef.current = true;
        const response = await axios.get(`${API_URL}/api/auth/ping`);
        
        // Always update the token in localStorage and axios headers
        if (response.data && response.data.token) {
          const newToken = response.data.token;
          const currentToken = localStorage.getItem('token');
          
          // Only update if token has actually changed to prevent unnecessary updates
          if (newToken !== currentToken) {
            localStorage.setItem('token', newToken);
            axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
            console.log('Token refreshed successfully');
          }
        }
        
        // Update user data if provided in response
        if (response.data && response.data.user) {
          setUser(response.data.user);
        }
      }
    } catch (error) {
      console.error('Error pinging server:', error);
      
      // Handle specific error cases
      if (error.response) {
        const status = error.response.status;
        
        // For 401 errors, let the interceptor handle it
        if (status === 401) {
          throw error; // Let the interceptor handle token refresh or logout
        }
        
        // For 503 errors, don't clear the token - server is temporarily unavailable
        if (status === 503) {
          console.warn('Server temporarily unavailable during ping');
          throw error;
        }
      }
      
      // For other errors, re-throw to let caller handle
      throw error;
    } finally {
      pingInProgressRef.current = false;
    }
  };

  useEffect(() => {
    // Check if user was in the process of logging out
    const isLoggingOut = localStorage.getItem('isLoggingOut');
    if (isLoggingOut === 'true') {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenTimestamp');
      localStorage.removeItem('isLoggingOut');
      localStorage.removeItem('sessionClosed');
      delete axios.defaults.headers.common['Authorization'];
      setLoading(false);
      return;
    }

    const token = localStorage.getItem('token');
    const sessionClosed = localStorage.getItem('sessionClosed');
    
    if (token) {
      // Check if session was closed (browser/tab closed) or token expired
      if (sessionClosed === 'true' || isTokenExpired()) {
        console.log('Session was closed or token expired, clearing authentication');
        localStorage.removeItem('token');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('sessionClosed');
        delete axios.defaults.headers.common['Authorization'];
        setLoading(false);
        return;
      }
      
      // Token exists and appears valid, set it and verify with server
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      checkAuth();
    } else {
      setLoading(false);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`);
      setUser(response.data);
      updateTokenTimestamp(); // Update timestamp on successful auth check
      localStorage.removeItem('sessionClosed'); // Clear the session closed flag
    } catch (error) {
      localStorage.removeItem('token');
      localStorage.removeItem('tokenTimestamp');
      localStorage.removeItem('sessionClosed');
      delete axios.defaults.headers.common['Authorization'];
    } finally {
      setLoading(false);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, {
        username,
        password,
      });
      
      // Check if password change is required for first-time login
      if (response.data.requiresPasswordChange) {
        // Return partial auth data for password change
        return {
          requiresPasswordChange: true,
          userId: response.data.userId,
          email: response.data.email
        };
      }
      // Check if 2FA is required
      else if (response.data.requiresTwoFactor) {
        // Return partial auth data for 2FA verification
        return {
          requiresTwoFactor: true,
          userId: response.data.userId,
          email: response.data.email,
          emailSent: response.data.emailSent,
          emailError: response.data.emailError,
          adminFallbackCode: response.data.adminFallbackCode
        };
      }
      
      // Normal login flow if 2FA not required
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      updateTokenTimestamp(); // Set initial timestamp
      localStorage.removeItem('sessionClosed'); // Clear any session closed flag
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Force browser refresh for iOS Safari to fix dropdown selection issues
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return user;
      }
      
      return user;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        // Handle both validation errors and invalid credentials
        const errorMessage = error.response.data.errors 
          ? error.response.data.errors[0].msg 
          : error.response.data.error;
        throw new Error(errorMessage || 'Invalid username or password');
      }
      throw new Error(error.response?.data?.error || error.message || 'Login failed');
    }
  };
  
  const verify2FA = async (userId, code) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/verify-2fa`, {
        userId,
        code
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      updateTokenTimestamp(); // Set initial timestamp
      localStorage.removeItem('sessionClosed'); // Clear any session closed flag
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Force browser refresh for iOS Safari to fix dropdown selection issues
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return user;
      }
      
      return user;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage = error.response.data.errors 
          ? error.response.data.errors[0].msg 
          : error.response.data.error;
        throw new Error(errorMessage || 'Invalid verification code');
      }
      throw new Error(error.response?.data?.error || error.message || 'Verification failed');
    }
  };
  
  const changePasswordFirstLogin = async (userId, newPassword) => {
    try {
      const response = await axios.post(`${API_URL}/api/auth/change-password-first-login`, {
        userId,
        newPassword
      });
      
      const { token, user } = response.data;
      localStorage.setItem('token', token);
      updateTokenTimestamp(); // Set initial timestamp
      localStorage.removeItem('sessionClosed'); // Clear any session closed flag
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setUser(user);
      
      // Force browser refresh for iOS Safari to fix dropdown selection issues
      if (/iPad|iPhone|iPod/.test(navigator.userAgent)) {
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return user;
      }
      
      return user;
    } catch (error) {
      if (error.response?.status === 401 || error.response?.status === 400) {
        const errorMessage = error.response.data.errors 
          ? error.response.data.errors[0].msg 
          : error.response.data.error;
        throw new Error(errorMessage || 'Invalid password');
      }
      throw new Error(error.response?.data?.error || error.message || 'Password change failed');
    }
  };
  
  const resend2FACode = async (userId) => {
    try {
      await axios.post(`${API_URL}/api/auth/resend-2fa`, { userId });
      return true;
    } catch (error) {
      throw new Error(error.response?.data?.error || error.message || 'Failed to resend verification code');
    }
  };

    const logout = async (callback) => {
        // Set logout flag immediately to prevent auto-login on app restart
        localStorage.setItem('isLoggingOut', 'true');
        
        // Store the token before clearing it for the logout API call
        const token = localStorage.getItem('token');
        
        // Immediately clear local storage, axios headers, and state first
        // This ensures logout happens even if the API call fails or is interrupted
        localStorage.removeItem('token');
        localStorage.removeItem('tokenTimestamp');
        localStorage.removeItem('sessionClosed');
        delete axios.defaults.headers.common['Authorization'];
        setUser(null);
        
        try {
            // Call the logout endpoint with the stored token
            if (token) {
                await axios.post(`${API_URL}/api/auth/logout`, null, {
                    headers: {
                        Authorization: `Bearer ${token}`
                    }
                });
            }
        } catch (error) {
            // Log error but don't throw - logout should succeed even if API call fails
            console.log('Logout API call failed (this is expected if token was already invalid):', error.message);
        } finally {
            // Clear the logout flag after everything is done
            localStorage.removeItem('isLoggingOut');
            
            // Execute callback after everything is done
            if (callback) callback();
        }
    };

  const value = {
    user,
    setUser, // Added setUser to the context value
    loading,
    login,
    logout,
    verify2FA,
    resend2FACode,
    changePasswordFirstLogin,
    pingServer // Expose pingServer for IdleTimeoutManager to use
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
